import React, { useState, useEffect, useCallback } from 'react';
import GenericModal from '../../genericModal/GenericModal';
import { useSelector } from 'react-redux';
import './slide1Edit.css';

const baseURL = process.env.REACT_APP_API_URL

// Fallback face images (keep as backup)
const face1 = require('../../../images/face1.png');
const face2 = require('../../../images/face2.png');
const face3 = require('../../../images/face3.png');
const face4 = require('../../../images/face4.png');
const face5 = require('../../../images/face5.png');

const Slide1Edit = ({ 
  selectedSlide,
  deckId,
  updatedDeckName,
  viewDeck,
  saveEditedSlide 
}) => {
  const [slideConfig, setSlideConfig] = useState({});
  const [initialState, setInitialState] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Icon set functionality - all in memory until save
  const [showIconSetModal, setShowIconSetModal] = useState(false);
  const [availableIconSets, setAvailableIconSets] = useState([]);
  const [selectedIconSetId, setSelectedIconSetId] = useState(null);
  const [currentIcons, setCurrentIcons] = useState([]);
  const [loadingIconSets, setLoadingIconSets] = useState(false);
  const [previewIcons, setPreviewIcons] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState({});
  const [showIconLabels, setShowIconLabels] = useState(false);
  const [iconLabels, setIconLabels] = useState({});
  const [stagedIconLabels, setStagedIconLabels] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [initialIconLabels, setInitialIconLabels] = useState({});

  const [stagedIconSetId, setStagedIconSetId] = useState(null);
  const [stagedShowIconLabels, setStagedShowIconLabels] = useState({});

  const { loading } = useSelector(state => state.slides);

  const fontOptions = [
    'Arial', 'Verdana', 'Georgia', 'Courier New', 'Sofia', 
    'Times New Roman', 'Poppins', 'Roboto', 'Open Sans'
  ];

  const backgroundColorOptions = [
    { color: '#fafafa', name: 'Default Light' },
    { color: '#f0f9ff', name: 'Light Blue' },
    { color: '#f0fdf4', name: 'Light Green' },
    { color: '#fefce8', name: 'Light Yellow' },
    { color: '#fdf2f8', name: 'Light Pink' },
    { color: '#f3f4f6', name: 'Light Gray' },
    { color: '#fef9c3', name: 'Light Cream' },
    { color: '#e0f2fe', name: 'Light Cyan' },
    { color: '#ecfdf5', name: 'Light Mint' },
    { color: '#fff7ed', name: 'Light Peach' },
    { color: '#f5f3ff', name: 'Light Lavender' },
    { color: '#f9fafb', name: 'Light Silver' }
  ];

  // Fallback face images for when no icon set is selected
  const fallbackIcons = [
    { sentiment_value: 5, icon_url: face1, alt_text: 'Very Happy' },
    { sentiment_value: 4, icon_url: face2, alt_text: 'Happy' },
    { sentiment_value: 3, icon_url: face3, alt_text: 'Neutral' },
    { sentiment_value: 2, icon_url: face4, alt_text: 'Sad' },
    { sentiment_value: 1, icon_url: face5, alt_text: 'Very Sad' }
  ];

  // ✅ Fetch available icon sets (only when modal opens)
  const fetchIconSets = async () => {
    setLoadingIconSets(true);
    try {
      const response = await fetch(`${baseURL}/icon-sets`);
      if (!response.ok) {
        throw new Error(`Failed to fetch icon sets: ${response.status}`);
      }
      const data = await response.json();
      setAvailableIconSets(data);
      console.log('Fetched icon sets:', data);
    } catch (error) {
      console.error('Error fetching icon sets:', error);
      setAvailableIconSets([]);
    } finally {
      setLoadingIconSets(false);
    }
  };

  // ✅ Fetch icons for a specific icon set
  const fetchIconsForSet = async (iconSetId) => {
    if (!iconSetId) return [];
    
    try {
      console.log(`Fetching icons for set: ${iconSetId}`);
      const response = await fetch(`${baseURL}/icon-sets/${iconSetId}/icons`);
      if (!response.ok) {
        throw new Error(`Failed to fetch icons for ${iconSetId}: ${response.status}`);
      }
      const data = await response.json();
      console.log(`Fetched ${data.icons?.length || 0} icons for ${iconSetId}:`, data);
      return data;
    } catch (error) {
      console.error('Error fetching icons for set:', iconSetId, error);
      return { icons: [] };
    }
  };

  // ✅ OPTIMIZED: Fetch preview icons on-demand (with debouncing)
  const fetchPreviewIconsForSet = useCallback(async (iconSetId) => {
    // ✅ Skip if already loaded or loading
    if (previewIcons[iconSetId] || loadingPreviews[iconSetId]) {
      console.log(`Skipping fetch for ${iconSetId} - already loaded/loading`);
      return;
    }

    console.log(`Starting fetch for ${iconSetId}`);
    setLoadingPreviews(prev => ({ ...prev, [iconSetId]: true }));
    
    try {
      const data = await fetchIconsForSet(iconSetId);
      setPreviewIcons(prev => ({ ...prev, [iconSetId]: data }));
    } catch (error) {
      console.error(`Error loading preview for ${iconSetId}:`, error);
      setPreviewIcons(prev => ({ ...prev, [iconSetId]: { icons: [] } }));
    } finally {
      setLoadingPreviews(prev => ({ ...prev, [iconSetId]: false }));
    }
  }, [previewIcons, loadingPreviews]); // ✅ Add dependencies

  // ✅ Load current icons based on selected icon set (only for the active slide)
  const loadCurrentIcons = async () => {
    if (selectedIconSetId && selectedIconSetId !== 'default_faces') {
      console.log(`Loading current icons for: ${selectedIconSetId}`);
      const iconsData = await fetchIconsForSet(selectedIconSetId);
      if (iconsData && iconsData.icons && iconsData.icons.length > 0) {
        setCurrentIcons(iconsData.icons);
      } else {
        console.log('No icons found, using fallback');
        setCurrentIcons(fallbackIcons);
      }
    } else {
      setCurrentIcons(fallbackIcons);
    }
  };

  // Effect 1: Sync state with props from the backend
  useEffect(() => {
    if (selectedSlide) {
      let parsedTextStyle = {};
      let parsedIconLabels = {};

      if (typeof selectedSlide.text_style === 'string') {
        try {
          parsedTextStyle = JSON.parse(selectedSlide.text_style);
        } catch (e) {
          console.error("Failed to parse text_style:", e);
          parsedTextStyle = {};
        }
      } else if (typeof selectedSlide.text_style === 'object' && selectedSlide.text_style !== null) {
        parsedTextStyle = selectedSlide.text_style;
      }

      if (typeof selectedSlide.icon_labels === 'string') {
        try {
          parsedIconLabels = JSON.parse(selectedSlide.icon_labels);
        } catch (e) {
          console.error("Failed to parse icon_labels:", e);
          parsedIconLabels = {};
        }
      } else if (typeof selectedSlide.icon_labels === 'object' && selectedSlide.icon_labels !== null) {
        parsedIconLabels = selectedSlide.icon_labels;
      }

      const state = {
        title: selectedSlide.title || '',
        fontFamily: parsedTextStyle.font_family || 'Arial',
        fontSize: parsedTextStyle.font_size || '36px',
        fontWeight: parsedTextStyle.font_weight || 'normal',
        fontStyle: parsedTextStyle.font_style || 'normal',
        fontColor: parsedTextStyle.color || '#000000',
        slideBackgroundColor: selectedSlide.background_color || '#171212ff',
      };

      setSlideConfig(state);
      setInitialState(state);
      
      // Initialize selected icon set from existing slide data
      const existingIconSetId = selectedSlide.icon_set_id || 'default_faces';
      setSelectedIconSetId(existingIconSetId);
      setShowIconLabels(selectedSlide.show_icon_labels || false);
      setIconLabels(parsedIconLabels);
      console.log('Initialized with icon set:', existingIconSetId);
      
      setHasChanges(false);
    }
  }, [selectedSlide]);

  // Effect 2: Compare current state with initial state to detect changes
  useEffect(() => {
    if (!initialState) return;

    let initialIconLabels = {};
    if (typeof selectedSlide.icon_labels === 'string') {
      try {
        initialIconLabels = JSON.parse(selectedSlide.icon_labels);
      } catch (e) {
        initialIconLabels = {};
      }
    } else if (typeof selectedSlide.icon_labels === 'object' && selectedSlide.icon_labels !== null) {
      initialIconLabels = selectedSlide.icon_labels;
    }

    const currentChanges =
      slideConfig.title !== initialState.title ||
      slideConfig.fontFamily !== initialState.fontFamily ||
      slideConfig.fontSize !== initialState.fontSize ||
      slideConfig.fontWeight !== initialState.fontWeight ||
      slideConfig.fontStyle !== initialState.fontStyle ||
      slideConfig.fontColor !== initialState.fontColor ||
      slideConfig.slideBackgroundColor !== initialState.slideBackgroundColor ||
      selectedIconSetId !== (selectedSlide?.icon_set_id || 'default_faces') ||
      JSON.stringify(iconLabels) !== JSON.stringify(initialIconLabels) ||
      showIconLabels !== (selectedSlide?.show_icon_labels || false);

    setHasChanges(currentChanges);
  }, [slideConfig, selectedIconSetId, initialState, selectedSlide, iconLabels, showIconLabels]);

  // Effect 3: Update CSS variables for live preview
  useEffect(() => {
    document.documentElement.style.setProperty('--slide1-bg-color', slideConfig.slideBackgroundColor);
    document.documentElement.style.setProperty('--slide1-color', slideConfig.fontColor);
    document.documentElement.style.setProperty('--slide1-font-family', slideConfig.fontFamily);
    document.documentElement.style.setProperty('--slide1-font-size', slideConfig.fontSize);
    document.documentElement.style.setProperty('--slide1-font-weight', slideConfig.fontWeight);
    document.documentElement.style.setProperty('--slide1-font-style', slideConfig.fontStyle);
  }, [slideConfig]);

  // Effect 4: Load current icons when selectedIconSetId changes
  useEffect(() => {
    loadCurrentIcons();
  }, [selectedIconSetId]);

  // Effect 5: Expand the category of the selected icon set when the modal is opened
  useEffect(() => {
    if (showIconSetModal && availableIconSets.length > 0) {
      const selectedSet = availableIconSets.find(set => set.icon_set_id === selectedIconSetId);
      const categoryToExpand = selectedSet?.category || 'faces'; // Default to faces if not found
      setExpandedCategory(categoryToExpand);
      
      // ✅ OPTIMIZED: Only fetch icons for the expanded category
      const setsInCategory = availableIconSets.filter(set => set.category === categoryToExpand);
      setsInCategory.forEach(iconSet => {
        fetchPreviewIconsForSet(iconSet.icon_set_id);
      });
    }
  }, [showIconSetModal, availableIconSets, selectedIconSetId, fetchPreviewIconsForSet]);

  // ✅ REMOVED: The problematic useEffect that fetched ALL icon sets
  // This was causing the connection leak!
  /*
  useEffect(() => {
    if (availableIconSets.length > 0) {
      availableIconSets.forEach(iconSet => {
        fetchPreviewIconsForSet(iconSet.icon_set_id); // ❌ BAD!
      });
    }
  }, [availableIconSets]);
  */

  const handleConfigChange = (key, value) => {
    setSlideConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleAdvancedSettingsClick = async () => {
    setShowIconSetModal(true);

    // Stage current settings
    setStagedIconSetId(selectedIconSetId);
    setStagedShowIconLabels(prev => ({ ...prev, [selectedIconSetId]: showIconLabels }));
    setStagedIconLabels(prev => ({ ...prev, [selectedIconSetId]: iconLabels }));
    setInitialIconLabels(prev => ({ ...prev, [selectedIconSetId]: iconLabels }));

    // ✅ Only fetch icon sets list if not already loaded
    if (availableIconSets.length === 0) {
      await fetchIconSets();
    }
  };

  const handleIconSetSelect = (iconSetId) => {
    setStagedIconSetId(iconSetId);
    
    // ✅ Fetch icons for this specific icon set when selected
    fetchPreviewIconsForSet(iconSetId);
    
    // Initialize staged show labels for this icon set if not exists
    if (stagedShowIconLabels[iconSetId] === undefined) {
      setStagedShowIconLabels(prev => ({ ...prev, [iconSetId]: false }));
    }
    
    if (!stagedIconLabels[iconSetId]) {
      const icons = previewIcons[iconSetId]?.icons || fallbackIcons;
      const initialLabels = {};
      icons.forEach(icon => {
        initialLabels[icon.sentiment_value] = icon.alt_text;
      });
      setStagedIconLabels(prev => ({ ...prev, [iconSetId]: initialLabels }));
    }
  };

  const handleConfirmIconSet = () => {
    setSelectedIconSetId(stagedIconSetId);
    setIconLabels(stagedIconLabels[stagedIconSetId] || {});
    setShowIconLabels(stagedShowIconLabels[stagedIconSetId] || false);
    setShowIconSetModal(false);
  };

  // ✅ NEW: Handle category expansion with lazy loading
  const handleCategoryToggle = (category) => {
    const newExpandedCategory = category === expandedCategory ? null : category;
    setExpandedCategory(newExpandedCategory);
    
    // ✅ Only fetch icons for the newly expanded category
    if (newExpandedCategory) {
      const setsInCategory = availableIconSets.filter(set => set.category === newExpandedCategory);
      console.log(`Expanding category: ${newExpandedCategory}, fetching ${setsInCategory.length} icon sets`);
      setsInCategory.forEach(iconSet => {
        fetchPreviewIconsForSet(iconSet.icon_set_id);
      });
    }
  };

  const handleSubmitSlide = () => {
    const updatedTextStyle = JSON.stringify({
      font_family: slideConfig.fontFamily,
      font_size: slideConfig.fontSize,
      font_weight: slideConfig.fontWeight,
      font_style: slideConfig.fontStyle,
      color: slideConfig.fontColor,
    });

    const updatedPostData = {
      slide_id: selectedSlide.slide_id,
      store_id: selectedSlide.store_id,
      slide_number: selectedSlide.slide_number,
      title: slideConfig.title,
      image_url: "",
      redirect_url: null,
      background_color: slideConfig.slideBackgroundColor,
      text_style: updatedTextStyle,
      deck_id: deckId,
      deck_name: updatedDeckName,
      email: localStorage.getItem('user'),
      type: 'rating',
      icon_set_id: selectedIconSetId || 'default_faces',
      show_icon_labels: showIconLabels,
      icon_labels: iconLabels
    };
    
    console.log('Saving slide with icon_set_id:', selectedIconSetId);
    saveEditedSlide(updatedPostData);
  };

  const getFontSizeLabel = () => {
    const size = parseInt((slideConfig.fontSize || '36px').replace('px', ''));
    if (size >= 52) return 'X-Large';
    if (size >= 44) return 'Large';
    return 'Medium';
  };

  const setFontSizeFromLabel = (label) => {
    const labelMap = {
      'Medium': '36px',
      'Large': '44px',
      'X-Large': '52px'
    };
    handleConfigChange('fontSize', labelMap[label] || '36px');
  };

  // Group icon sets by category for better organization
  const groupedIconSets = availableIconSets.reduce((groups, iconSet) => {
    const category = iconSet.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(iconSet);
    return groups;
  }, {});

  const renderFaceButtons = () => {
    const iconsToRender = currentIcons.icons || currentIcons;
    return iconsToRender.map((icon) => (
      <button key={icon.sentiment_value} disabled className='slide1-face-btn'>
        <img 
          src={icon.icon_url} 
          alt={icon.alt_text} 
          className='slide1-face-img'
          onError={(e) => {
            console.log('Icon failed to load:', icon.icon_url);
            // Fallback to default face if icon fails to load
            const fallback = fallbackIcons.find(f => f.sentiment_value === icon.sentiment_value);
            if (fallback) {
              console.log('Using fallback:', fallback.icon_url);
              e.target.src = fallback.icon_url;
            }
          }}
        />
        {showIconLabels && <span className="face-label">{iconLabels[icon.sentiment_value]}</span>}
      </button>
    ));
  };

  // Render icon set card with preview
  const renderIconSetCard = (iconSet) => {
    const isSelected = stagedIconSetId === iconSet.icon_set_id;
    const icons = previewIcons[iconSet.icon_set_id] || { icons: [] };
    const isLoadingPreview = loadingPreviews[iconSet.icon_set_id];
    const currentShowLabels = stagedShowIconLabels[iconSet.icon_set_id] || false;

    return (
      <div
        key={iconSet.icon_set_id}
        className={`icon-set-card ${isSelected ? 'selected' : ''}`}
        onClick={() => handleIconSetSelect(iconSet.icon_set_id)}
        // ✅ Optional: Fetch on hover for better UX
        onMouseEnter={() => fetchPreviewIconsForSet(iconSet.icon_set_id)}
      >
        <div className="icon-set-header">
          <h4 className="icon-set-name">{iconSet.name}</h4>
          <p className="icon-set-description">{iconSet.description}</p>
          <span className="icon-count-badge">{iconSet.icon_count} icons</span>
        </div>
        
        <div className="icon-set-preview">
          {isLoadingPreview ? (
            <div className="preview-loading">
              <div className="loading-spinner"></div>
              <span>Loading preview...</span>
            </div>
          ) : icons?.icons?.length > 0 ? (
            <div className="preview-icons-row">
              {icons.icons.map((icon) => (
                <div key={icon.sentiment_value} className="preview-icon-container">
                  <img
                    src={icon.icon_url}
                    alt={icon.alt_text}
                    className="preview-icon-img"
                    onLoad={() => console.log(`Icon loaded: ${icon.icon_url}`)}
                    onError={(e) => {
                      console.error(`Failed to load icon: ${icon.icon_url}`);
                      e.target.style.display = 'none';
                    }}
                  />
                  <span className="preview-icon-label">{icon.sentiment_value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="preview-error">
              <span>No preview available</span>
            </div>
          )}
        </div>
        <div className="icon-set-actions">
        <div className="show-labels-toggle">
          <input
            type="checkbox"
            id={`show-labels-${iconSet.icon_set_id}`}
            checked={currentShowLabels}
            onChange={(e) => {
              e.stopPropagation();
              const isChecked = e.target.checked;

              if (isChecked && !stagedIconLabels[iconSet.icon_set_id]) {
                const icons = previewIcons[iconSet.icon_set_id]?.icons || fallbackIcons;
                const initialLabels = {};
                icons.forEach(icon => {
                  initialLabels[icon.sentiment_value] = icon.alt_text;
                });
                setStagedIconLabels(prev => ({ 
                  ...prev, 
                  [iconSet.icon_set_id]: initialLabels 
                }));
              }

              setStagedShowIconLabels(prev => ({ 
                ...prev, 
                [iconSet.icon_set_id]: e.target.checked 
              }));
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <label 
            htmlFor={`show-labels-${iconSet.icon_set_id}`}
            onClick={(e) => e.stopPropagation()}
          >
            Show text under icons
          </label>
        </div>
        {currentShowLabels && (
          <div className="icon-labels-editor">
            {icons?.icons?.map(icon => (
              <div key={icon.sentiment_value} className="label-editor-row">
                <label>{`Icon ${icon.sentiment_value}:`}</label>
                <input
                  type="text"
                  value={stagedIconLabels[iconSet.icon_set_id]?.[icon.sentiment_value] || ''}
                  onChange={(e) => {
                    e.stopPropagation();
                    setStagedIconLabels(prev => ({ 
                      ...prev, 
                      [iconSet.icon_set_id]: { 
                        ...prev[iconSet.icon_set_id], 
                        [icon.sentiment_value]: e.target.value 
                      } 
                    }));
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      
      {isSelected && (
        <div className="selected-indicator">
          <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
  };

  const haveLabelsChanged = () => {
    if (!stagedIconSetId) return false;
    const originalLabels = initialIconLabels[stagedIconSetId] || {};
    const stagedLabels = stagedIconLabels[stagedIconSetId] || {};
    const originalShowLabels = selectedIconSetId === stagedIconSetId ? showIconLabels : false;
    const currentShowLabels = stagedShowIconLabels[stagedIconSetId] || false;
    
    return JSON.stringify(originalLabels) !== JSON.stringify(stagedLabels) ||
           originalShowLabels !== currentShowLabels;
  };

  return (
    <>
      <div className='slide1-container-updated'>
        <div className='slide1-sidebar'>
          <div className='slide1-section'>
            <div className='slide1-question-title-header'>
              <h3 className='slide1-section-title'>Question Title</h3>
              {!viewDeck && (
                <button
                  onClick={handleAdvancedSettingsClick}
                  className='slide1-advanced-settings-btn'
                  title="Update your feedback icons"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
            
            <div className='slide1-question-input-container'>
              <div className='slide1-question-display'>
                <input
                  type='text'
                  value={slideConfig.title || ''}
                  onChange={(e) => handleConfigChange('title', e.target.value)}
                  className='slide1-question-input'
                  placeholder='Enter question title...'
                  disabled={viewDeck}
                />
              </div>
            </div>
          </div>

          <div className='slide1-section'>
            <h3 className='slide1-section-title'>Font Size</h3>
            <div className='slide1-font-size-buttons'>
              {['Medium', 'Large', 'X-Large'].map(size => (
                <button
                  key={size}
                  onClick={() => setFontSizeFromLabel(size)}
                  className={`slide1-font-size-btn ${getFontSizeLabel() === size ? 'slide1-font-size-btn-active' : ''}`}
                  disabled={viewDeck}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <div className='slide1-font-style-color-container'>
            <div className='slide1-section'>
              <h3 className='slide1-section-title'>Font Family</h3>
              <select
                value={slideConfig.fontFamily || 'Arial'}
                onChange={(e) => handleConfigChange('fontFamily', e.target.value)}
                className='slide1-font-select'
                disabled={viewDeck}
              >
                {fontOptions.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>
            <div className='slide1-section'>
              <h3 className='slide1-section-title'>Font Style</h3>
              <div className='slide1-font-style-buttons'>
                <button
                  onClick={() => handleConfigChange('fontWeight', slideConfig.fontWeight === 'bold' ? 'normal' : 'bold')}
                  className={`slide1-style-btn ${slideConfig.fontWeight === 'bold' ? 'slide1-style-btn-active' : ''}`}
                  disabled={viewDeck}
                >
                  <strong>B</strong>
                </button>
                <button
                  onClick={() => handleConfigChange('fontStyle', slideConfig.fontStyle === 'italic' ? 'normal' : 'italic')}
                  className={`slide1-style-btn ${slideConfig.fontStyle === 'italic' ? 'slide1-style-btn-active' : ''}`}
                  disabled={viewDeck}
                >
                  <em>I</em>
                </button>
              </div>
            </div>

            <div className='slide1-section'>
              <h3 className='slide1-section-title'>Font Color</h3>
              <input
                type='color'
                value={slideConfig.fontColor || '#000000'}
                onChange={(e) => handleConfigChange('fontColor', e.target.value)}
                className='slide1-color-input'
                disabled={viewDeck}
              />
            </div>
          </div>

          <div className='slide1-section'>
            <h3 className='slide1-section-title'>Background Color</h3>
            <div className='slide1-background-color-grid'>
              {backgroundColorOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleConfigChange('slideBackgroundColor', option.color)}
                  className={`slide1-background-color-option ${slideConfig.slideBackgroundColor === option.color ? 'slide1-background-color-active' : ''}`}
                  disabled={viewDeck}
                  title={option.name}
                >
                  <div 
                    className='slide1-background-color-preview' 
                    style={{ backgroundColor: option.color }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className='slide1-action-buttons'>
            {!viewDeck && (
              <>
                <button 
                  onClick={handleSubmitSlide} 
                  className='slide1-btn slide1-btn-primary'
                  disabled={loading || !hasChanges}
                >
                  {loading ? 'Updating...' : '📋 Update Deck'}
                </button>
                <button 
                  onClick={() => setShowPreview(true)} 
                  className='slide1-btn slide1-btn-secondary'
                >
                  👁 Preview
                </button>
              </>
            )}
          </div>
        </div>

        <div className='slide1-preview'>
          <div 
            className='slide1-preview-content'
            style={{ backgroundColor: slideConfig.slideBackgroundColor }}
          >
            <div className='slide1-preview-title'>
              <div 
                style={{ 
                  fontFamily: slideConfig.fontFamily,
                  fontSize: slideConfig.fontSize,
                  fontWeight: slideConfig.fontWeight,
                  fontStyle: slideConfig.fontStyle,
                  color: slideConfig.fontColor,
                  textAlign: 'center',
                  marginBottom: '40px'
                }}
              >
                {slideConfig.title}
              </div>
            </div>
            
            <div className='slide1-faces-container'>
              <div className='slide1-faces-row'>
                {renderFaceButtons()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <GenericModal size='lg' show={showPreview} onClose={() => setShowPreview(false)}
        header={
          <div className="custom-modal-header">
            <div className="modern-preview-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h1 className='slide1-main-title'>Slide Preview</h1>
            </div>
          </div>
        }
        body={
          <div 
            className='slide1-preview-content'
            style={{ backgroundColor: slideConfig.slideBackgroundColor, height: '400px' }}
          >
            <div className='slide1-preview-title'>
              <div 
                style={{ 
                  fontFamily: slideConfig.fontFamily,
                  fontSize: slideConfig.fontSize,
                  fontWeight: slideConfig.fontWeight,
                  fontStyle: slideConfig.fontStyle,
                  color: slideConfig.fontColor,
                  textAlign: 'center',
                  marginBottom: '40px'
                }}
              >
                {slideConfig.title}
              </div>
            </div>
            
            <div className='slide1-faces-container'>
              <div className='slide1-faces-row'>
                {renderFaceButtons()}
              </div>
            </div>
          </div>
        }
      />

      {/* Enhanced Icon Set Selection Modal */}
      <GenericModal 
        size='xl' 
        show={showIconSetModal} 
        onClose={() => setShowIconSetModal(false)}
        header={
          <div className="custom-modal-header">
            <div className="modern-preview-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5H9a2 2 0 00-2 2v12a4 4 0 004 4h6a2 2 0 002-2V7a2 2 0 00-2-2z" />
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h1 className='slide1-main-title'>
                Choose Feedback Icons
                {stagedIconSetId && availableIconSets.find(set => set.icon_set_id === stagedIconSetId) &&
                  <span className="selected-set-indicator"> - {availableIconSets.find(set => set.icon_set_id === stagedIconSetId).name}</span>
                }
              </h1>
              <p className='slide1-subtitle'>Select an icon set for your feedback screen. Click a category to expand and preview icons.</p>
            </div>
          </div>
        }
        body={
          <div className="icon-set-selector-container">
            {loadingIconSets ? (
              <div className="icon-set-loading">
                <div className="loading-spinner-large">Loading icon sets...</div>
              </div>
            ) : availableIconSets.length === 0 ? (
              <div className="no-icon-sets">
                <p>No icon sets available. Please contact support to add icon sets.</p>
              </div>
            ) : (
              <div className="icon-sets-wrapper">
                {Object.entries(groupedIconSets).sort(([catA], [catB]) => catA.localeCompare(catB)).map(([category, iconSets]) => {
                  const selectedInCategory = iconSets.find(set => set.icon_set_id === stagedIconSetId);
                  
                  
                  return (
                    <div key={category} className="icon-category-section">
                      <h3 
                        className="icon-category-title" 
                        onClick={() => handleCategoryToggle(category)} // ✅ Use new handler
                      >
                        
                        {category.charAt(0).toUpperCase() + category.slice(1)} ({iconSets.length})
                        <span className="expand-arrow">{expandedCategory === category ? '▼' : '▶'}</span>
                      </h3>
                      {expandedCategory === category && (
                        <div className="icon-sets-grid">
                          {iconSets.map(renderIconSetCard)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        }
        footer={
          <div className="icon-set-modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowIconSetModal(false)}>Cancel</button>
            <button 
              className="btn btn-primary"
              disabled={!stagedIconSetId || (stagedIconSetId === selectedIconSetId && !haveLabelsChanged())}
              onClick={handleConfirmIconSet}
            >
              Confirm
            </button>
          </div>
        }
      />
    </>
  );
};

export default Slide1Edit;